import { Link, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "@/lib/public-icons";

/* A page whose code changed under an open tab fails to fetch its chunk; the
   fix is a reload, not "try again", which asks for the same missing file. */
const STALE = /dynamically imported module|Importing a module script failed|Loading chunk/i;

/** A route that threw: what happened in words, the error itself as a detail, and two ways on. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : "";
  const stale = STALE.test(message);
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="bg-crit-fill text-crit-ink grid size-10 place-items-center rounded-lg">
        <AlertCircle className="size-5" aria-hidden="true" />
      </span>
      <h1 className="text-2xl font-semibold tracking-tight text-balance">
        {stale ? "People AI was updated" : "This page couldn't load"}
      </h1>
      <p className="text-muted-foreground text-sm text-pretty">
        {stale
          ? "Reload the page to get the latest version. Nothing you saved is lost."
          : "Try again. If it keeps happening, go back to the dashboard and open it from there."}
      </p>
      {message && !stale ? (
        <p className="bg-sunken text-muted-foreground max-w-full rounded-md border px-3 py-2 text-xs break-words">
          {message}
        </p>
      ) : null}
      <div className="mt-1 flex flex-wrap justify-center gap-2">
        {stale ? (
          <Button type="button" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        ) : (
          <Button type="button" onClick={reset}>
            Try again
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/">Back to the dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
